import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { trainerApi, type CalendarBookingDto, type DayOfWeekName, type TrainerUnavailabilityDto } from '@/api/trainer'
import { ChevronLeft, ChevronRight, Users, CalendarDays, CalendarOff, Copy, Check, RefreshCw, X } from 'lucide-react'
import { useTrainerDates } from '@/hooks/useDates'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const ALL_DAYS: { name: DayOfWeekName; label: string }[] = [
  { name: 'Monday', label: 'Mon' }, { name: 'Tuesday', label: 'Tue' }, { name: 'Wednesday', label: 'Wed' },
  { name: 'Thursday', label: 'Thu' }, { name: 'Friday', label: 'Fri' }, { name: 'Saturday', label: 'Sat' }, { name: 'Sunday', label: 'Sun' },
]

/** True if dateStr (YYYY-MM-DD) falls within [u.startDate, u.endDate] inclusive. */
function covers(u: TrainerUnavailabilityDto, dateStr: string): boolean {
  return dateStr >= u.startDate && dateStr <= u.endDate
}

// ─── Mark unavailable modal ────────────────────────────────────────────────────────

function MarkUnavailableModal({ initialDate, onClose, onSaved }: {
  initialDate: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const [startDate, setStartDate] = useState(initialDate ?? '')
  const [endDate, setEndDate] = useState(initialDate ?? '')
  const [reason, setReason] = useState('')
  const [repeats, setRepeats] = useState(false)
  const [days, setDays] = useState<Set<DayOfWeekName>>(new Set())
  const [recurringEndDate, setRecurringEndDate] = useState('')

  const rangeMutation = useMutation({
    mutationFn: () => trainerApi.addMyUnavailability({ startDate, endDate, reason: reason.trim() || null }),
    onSuccess: onSaved,
  })
  const recurringMutation = useMutation({
    mutationFn: () => trainerApi.addMyRecurringUnavailability({
      daysOfWeek: [...days], startDate, endDate: recurringEndDate || null, reason: reason.trim() || null,
    }),
    onSuccess: onSaved,
  })

  const toggleDay = (d: DayOfWeekName) =>
    setDays(prev => { const n = new Set(prev); n.has(d) ? n.delete(d) : n.add(d); return n })

  const canSave = repeats
    ? !!startDate && days.size > 0 && !recurringMutation.isPending
    : !!startDate && !!endDate && endDate >= startDate && !rangeMutation.isPending

  const inputCls = "h-9 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b">
          <h2 className="text-base font-semibold text-gray-900">Mark unavailable</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 cursor-pointer"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={repeats} onChange={e => setRepeats(e.target.checked)} className="w-4 h-4" />
            Repeats weekly
          </label>

          {repeats ? (
            <>
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">On these days</p>
                <div className="flex gap-1 flex-wrap">
                  {ALL_DAYS.map(d => (
                    <button
                      key={d.name} type="button" onClick={() => toggleDay(d.name)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer transition-colors ${
                        days.has(d.name) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Until (optional)</label>
                  <input type="date" value={recurringEndDate} onChange={e => setRecurringEndDate(e.target.value)} className={inputCls} />
                </div>
              </div>
              <p className="text-xs text-gray-400">Runs for up to 12 months if no end date is set.</p>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">From</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">To</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Reason (optional)</label>
            <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Annual leave" className={inputCls} />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer">
            Cancel
          </button>
          <button
            onClick={() => repeats ? recurringMutation.mutate() : rangeMutation.mutate()}
            disabled={!canSave}
            className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {(rangeMutation.isPending || recurringMutation.isPending) ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

function statusColour(status: CalendarBookingDto['status']) {
  switch (status) {
    case 'Booked':
    case 'Published':  return 'bg-indigo-100 text-indigo-800 border-indigo-200'
    case 'Completed':  return 'bg-green-100 text-green-800 border-green-200'
    default:           return 'bg-gray-100 text-gray-600 border-gray-200'
  }
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month, 0).getDate()
}

// weekStartsOn: 0 = Sunday-first (US), 1 = Monday-first
function getStartDayOfWeek(year: number, month: number, weekStartsOn: 0 | 1) {
  const d = new Date(year, month - 1, 1).getDay()
  return (d - weekStartsOn + 7) % 7
}

function getWeekStart(weekStartsOn: 0 | 1): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() - ((d.getDay() - weekStartsOn + 7) % 7))
  return d
}

function addDays(date: Date, n: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

function toDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Compact chip for month/week grid cells
function BookingChip({ b, basePath, navigate }: { b: CalendarBookingDto; basePath: string; navigate: (path: string) => void }) {
  const route = b.eventType === 'Session' ? `${basePath}/sessions/${b.id}` : `${basePath}/bookings/${b.id}`

  return (
    <button
      type="button"
      onClick={e => { e.stopPropagation(); navigate(route) }}
      className={`w-full text-left px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border text-xs leading-tight cursor-pointer hover:opacity-80 transition-opacity ${statusColour(b.status)}`}
    >
      <div className="flex items-center gap-1">
        <span className="font-medium truncate">{b.courseName}</span>
        {b.eventType === 'Session' && <span className="shrink-0 text-[9px] opacity-50 font-normal">public</span>}
        {b.dayNumber != null && (
          <span className="shrink-0 text-[9px] opacity-60 font-normal">D{b.dayNumber}/{b.totalDays}</span>
        )}
      </div>
      {b.clientName && <div className="hidden sm:block text-[10px] opacity-75 truncate">{b.clientName}</div>}
      <div className="hidden sm:flex items-center justify-between mt-0.5">
        {b.trainers.length > 1 && (
          <span className="text-[10px] opacity-70">+{b.trainers.length - 1} other{b.trainers.length > 2 ? 's' : ''}</span>
        )}
        <div className="flex items-center gap-0.5 text-[10px] opacity-60 ml-auto">
          <Users size={9} /><span>{b.places}</span>
        </div>
      </div>
    </button>
  )
}

// Full card for the day panel and mobile week list
function BookingCard({ b, basePath, navigate }: { b: CalendarBookingDto; basePath: string; navigate: (path: string) => void }) {
  const cardClass = (b.status === 'Booked' || b.status === 'Published') ? 'border-indigo-200 bg-indigo-50' :
    b.status === 'Completed' ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
  const route = b.eventType === 'Session' ? `${basePath}/sessions/${b.id}` : `${basePath}/bookings/${b.id}`

  return (
    <button
      type="button"
      onClick={() => navigate(route)}
      className={`w-full text-left px-3 py-2.5 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity ${cardClass}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm text-gray-900 truncate">{b.courseName}</span>
            {b.eventType === 'Session' && <span className="text-xs text-gray-400 font-normal shrink-0">Public</span>}
            {b.dayNumber != null && (
              <span className="shrink-0 text-xs text-gray-400 font-normal">Day {b.dayNumber}/{b.totalDays}</span>
            )}
          </div>
          {b.clientName && <p className="text-xs text-gray-500 truncate mt-0.5">{b.clientName}</p>}
          {b.trainers.length > 1 && (
            <p className="text-xs text-gray-500 mt-1">With {b.trainers.length - 1} other trainer{b.trainers.length > 2 ? 's' : ''}</p>
          )}
        </div>
        <div className="flex items-center gap-0.5 text-xs shrink-0 mt-0.5 text-gray-500">
          <Users size={11} />
          <span>{b.places}</span>
        </div>
      </div>
    </button>
  )
}

export default function TrainerCalendarPage({ basePath = '/trainer' }: { basePath?: string }) {
  const { formatDate, weekStartsOn } = useTrainerDates()
  const dayNames = weekStartsOn === 0 ? [DAY_NAMES[6], ...DAY_NAMES.slice(0, 6)] : DAY_NAMES
  const formatDayHeading = (dateStr: string): string => {
    return formatDate(dateStr + 'T12:00:00', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })
  }

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const today = new Date()

  const [view, setView] = useState<'month' | 'week'>('month')
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth() + 1)
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(weekStartsOn))
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [showSubscribe, setShowSubscribe] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showMarkTimeOff, setShowMarkTimeOff] = useState(false)

  const { data: unavailability = [] } = useQuery({
    queryKey: ['trainer-unavailability'],
    queryFn: () => trainerApi.getMyUnavailability().then(r => r.data),
  })
  const unavailableForDate = (dateStr: string) => unavailability.filter(u => covers(u, dateStr))

  const removeUnavailabilityMutation = useMutation({
    mutationFn: (id: string) => trainerApi.removeMyUnavailability(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-unavailability'] }),
  })
  const removeUnavailabilityGroupMutation = useMutation({
    mutationFn: (groupId: string) => trainerApi.removeMyUnavailabilityGroup(groupId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-unavailability'] }),
  })
  const quickAddMutation = useMutation({
    mutationFn: (dateStr: string) => trainerApi.addMyUnavailability({ startDate: dateStr, endDate: dateStr, reason: null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-unavailability'] }),
  })

  useEffect(() => { setWeekStart(getWeekStart(weekStartsOn)) }, [weekStartsOn])

  const weekEnd  = useMemo(() => addDays(weekStart, 6), [weekStart])
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart])

  const wsYear = weekStart.getFullYear(), wsMonth = weekStart.getMonth() + 1
  const weYear = weekEnd.getFullYear(),   weMonth = weekEnd.getMonth() + 1
  const weekSpansMonths = wsYear !== weYear || wsMonth !== weMonth

  const { data: monthBookings = [], isLoading: monthLoading } = useQuery({
    queryKey: ['trainer-calendar', year, month],
    queryFn: () => trainerApi.getMyCalendar(year, month).then(r => r.data),
    enabled: view === 'month',
  })

  const { data: weekBookings1 = [], isLoading: weekLoading } = useQuery({
    queryKey: ['trainer-calendar', wsYear, wsMonth],
    queryFn: () => trainerApi.getMyCalendar(wsYear, wsMonth).then(r => r.data),
    enabled: view === 'week',
  })

  const { data: weekBookings2 = [] } = useQuery({
    queryKey: ['trainer-calendar', weYear, weMonth],
    queryFn: () => trainerApi.getMyCalendar(weYear, weMonth).then(r => r.data),
    enabled: view === 'week' && weekSpansMonths,
  })

  const isLoading = view === 'month' ? monthLoading : weekLoading

  const bookings = useMemo(() => {
    if (view === 'month') return monthBookings
    return weekSpansMonths ? [...weekBookings1, ...weekBookings2] : weekBookings1
  }, [view, monthBookings, weekBookings1, weekBookings2, weekSpansMonths])

  const { data: tokenData } = useQuery({
    queryKey: ['trainer-calendar-feed-token'],
    queryFn: () => trainerApi.getCalendarFeedToken().then(r => r.data),
    enabled: showSubscribe,
  })

  const generateMutation = useMutation({
    mutationFn: () => trainerApi.generateCalendarFeedToken(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trainer-calendar-feed-token'] }),
  })

  const apiBase = import.meta.env.VITE_API_URL ?? window.location.origin
  const feedUrl = tokenData?.token ? `${apiBase}/api/v1/calendar/trainer-feed?token=${tokenData.token}` : null

  const copyUrl = () => {
    if (!feedUrl) return
    navigator.clipboard.writeText(feedUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const prevMonth = () => { setSelectedDate(null); if (month === 1) { setYear(y => y - 1); setMonth(12) } else setMonth(m => m - 1) }
  const nextMonth = () => { setSelectedDate(null); if (month === 12) { setYear(y => y + 1); setMonth(1) } else setMonth(m => m + 1) }
  const prevWeek  = () => { setSelectedDate(null); setWeekStart(w => addDays(w, -7)) }
  const nextWeek  = () => { setSelectedDate(null); setWeekStart(w => addDays(w, 7)) }

  const goToday = () => {
    setSelectedDate(null)
    if (view === 'month') { setYear(today.getFullYear()); setMonth(today.getMonth() + 1) }
    else setWeekStart(getWeekStart(weekStartsOn))
  }

  const switchView = (v: 'month' | 'week') => {
    setView(v)
    setSelectedDate(null)
    if (v === 'week') {
      const monthHasToday = today.getFullYear() === year && today.getMonth() + 1 === month
      if (monthHasToday) {
        setWeekStart(getWeekStart(weekStartsOn))
      } else {
        const firstOfMonth = new Date(year, month - 1, 1)
        const dow = firstOfMonth.getDay()
        setWeekStart(addDays(firstOfMonth, -((dow - weekStartsOn + 7) % 7)))
      }
    } else {
      setYear(weekStart.getFullYear())
      setMonth(weekStart.getMonth() + 1)
    }
  }

  const toggleDate = (dateStr: string) => setSelectedDate(d => d === dateStr ? null : dateStr)

  const daysInMonth = getDaysInMonth(year, month)
  const startDow   = getStartDayOfWeek(year, month, weekStartsOn)
  const totalCells = Math.ceil((startDow + daysInMonth) / 7) * 7

  const byDay = useMemo(() => {
    const r: Record<number, CalendarBookingDto[]> = {}
    if (view !== 'month') return r
    for (const b of bookings) {
      const d = parseInt(b.bookingDate.split('-')[2])
      ;(r[d] ??= []).push(b)
    }
    return r
  }, [bookings, view])

  const byDateStr = useMemo(() => {
    const r: Record<string, CalendarBookingDto[]> = {}
    for (const b of bookings) (r[b.bookingDate] ??= []).push(b)
    return r
  }, [bookings])

  const selectedDateBookings = selectedDate ? (byDateStr[selectedDate] ?? []) : []

  const todayStr = toDateStr(today)

  const headerLabel = useMemo(() => {
    if (view === 'month') return `${MONTH_NAMES[month - 1]} ${year}`
    const s = weekStart, e = weekEnd
    const sm = MONTH_NAMES[s.getMonth()].slice(0, 3)
    const em = MONTH_NAMES[e.getMonth()].slice(0, 3)
    if (s.getFullYear() !== e.getFullYear())
      return `${s.getDate()} ${sm} ${s.getFullYear()} – ${e.getDate()} ${em} ${e.getFullYear()}`
    if (s.getMonth() !== e.getMonth())
      return `${s.getDate()} ${sm} – ${e.getDate()} ${em} ${s.getFullYear()}`
    return `${s.getDate()}–${e.getDate()} ${sm} ${s.getFullYear()}`
  }, [view, year, month, weekStart, weekEnd])

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">My Calendar</h1>
        <p className="text-sm text-gray-500 mt-0.5">Everything you're assigned to deliver.</p>
      </div>

      {/* Header — stacks on mobile, inline on sm+ */}
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-1.5">
          <button onClick={view === 'month' ? prevMonth : prevWeek} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 cursor-pointer transition-colors">
            <ChevronLeft size={18} />
          </button>
          <span className="text-sm font-semibold text-gray-800 text-center min-w-[152px]">{headerLabel}</span>
          <button onClick={view === 'month' ? nextMonth : nextWeek} className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 cursor-pointer transition-colors">
            <ChevronRight size={18} />
          </button>
          <button onClick={goToday} className="ml-1 px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors">
            Today
          </button>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
            <button
              onClick={() => switchView('month')}
              className={`px-3 py-1.5 cursor-pointer transition-colors ${view === 'month' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Month
            </button>
            <button
              onClick={() => switchView('week')}
              className={`px-3 py-1.5 border-l border-gray-200 cursor-pointer transition-colors ${view === 'week' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Week
            </button>
          </div>

          <button
            onClick={() => setShowMarkTimeOff(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <CalendarOff size={15} />
            <span className="hidden sm:inline">Mark unavailable</span>
          </button>

          <button
            onClick={() => setShowSubscribe(s => !s)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer transition-colors"
          >
            <CalendarDays size={15} />
            <span className="hidden sm:inline">Subscribe</span>
          </button>
        </div>
      </div>

      {showMarkTimeOff && (
        <MarkUnavailableModal
          initialDate={selectedDate}
          onClose={() => setShowMarkTimeOff(false)}
          onSaved={() => { setShowMarkTimeOff(false); queryClient.invalidateQueries({ queryKey: ['trainer-unavailability'] }) }}
        />
      )}

      {/* Subscribe panel */}
      {showSubscribe && (
        <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-xl p-4 space-y-3">
          <div>
            <p className="text-sm font-semibold text-indigo-900">Subscribe in Outlook (or any calendar app)</p>
            <p className="text-xs text-indigo-600 mt-0.5">
              Copy the link below and paste it into Outlook → Add calendar → Subscribe from web.
              Your calendar will stay in sync automatically.
            </p>
          </div>
          {!tokenData?.token && !generateMutation.isPending && (
            <button
              onClick={() => generateMutation.mutate()}
              className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer transition-colors"
            >
              Generate subscription link
            </button>
          )}
          {generateMutation.isPending && <p className="text-xs text-indigo-500">Generating…</p>}
          {feedUrl && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  readOnly value={feedUrl}
                  className="flex-1 min-w-0 text-xs font-mono bg-white border border-indigo-200 rounded-lg px-3 py-2 text-gray-700 select-all"
                  onFocus={e => e.target.select()}
                />
                <button
                  onClick={copyUrl}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-100 cursor-pointer transition-colors whitespace-nowrap shrink-0"
                >
                  {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                </button>
              </div>
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs text-indigo-500">
                  Treat this link like a password — anyone with it can view your schedule.
                </p>
                <button
                  onClick={() => generateMutation.mutate()}
                  disabled={generateMutation.isPending}
                  className="flex items-center gap-1 text-xs text-indigo-500 hover:text-indigo-700 cursor-pointer transition-colors shrink-0"
                  title="Generate a new link (invalidates the old one)"
                >
                  <RefreshCw size={12} /> Regenerate
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        {(['Booked', 'Completed'] as const).map(s => (
          <span key={s} className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs border ${statusColour(s)}`}>
            {s}
          </span>
        ))}
      </div>

      {/* ── MONTH VIEW ── */}
      {view === 'month' && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-7 border-b border-gray-100">
            {dayNames.map(d => (
              <div key={d} className="py-2.5 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {d}
              </div>
            ))}
          </div>
          {isLoading ? (
            <div className="py-24 text-center text-gray-400 text-sm">Loading…</div>
          ) : (
            <div className="grid grid-cols-7">
              {Array.from({ length: totalCells }).map((_, idx) => {
                const day = idx - startDow + 1
                const isCurrentMonth = day >= 1 && day <= daysInMonth
                const dateStr = isCurrentMonth
                  ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
                  : ''
                const isToday = dateStr === todayStr
                const isSelected = dateStr !== '' && dateStr === selectedDate
                const dayBookings = isCurrentMonth ? (byDay[day] ?? []) : []
                const isWeekend = (idx % 7) >= 5
                const isUnavailable = isCurrentMonth && unavailableForDate(dateStr).length > 0

                return (
                  <div
                    key={idx}
                    className={`min-h-[80px] sm:min-h-[120px] border-b border-r border-gray-100 p-1 sm:p-2 last:border-r-0 ${
                      isSelected ? 'bg-indigo-50/60' :
                      !isCurrentMonth ? 'bg-gray-50/50' :
                      isUnavailable ? 'bg-gray-100' :
                      isWeekend ? 'bg-slate-50/60' : 'bg-white'
                    }`}
                  >
                    {isCurrentMonth && (
                      <>
                        <div
                          onClick={() => toggleDate(dateStr)}
                          className={`text-xs font-semibold mb-1 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full cursor-pointer select-none ${
                            isToday ? 'bg-indigo-600 text-white' :
                            isSelected ? 'bg-indigo-200 text-indigo-900' :
                            'text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          {day}
                        </div>
                        {isUnavailable && (
                          <div className="flex items-center gap-1 text-[9px] text-gray-400 mb-0.5">
                            <CalendarOff size={9} /><span className="hidden sm:inline">Unavailable</span>
                          </div>
                        )}
                        <div className="space-y-0.5">
                          {dayBookings.map(b => <BookingChip key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} basePath={basePath} navigate={navigate} />)}
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── WEEK VIEW ── */}
      {view === 'week' && (
        <>
          {/* Mobile: stacked day list */}
          <div className="sm:hidden space-y-5">
            {isLoading && <p className="text-center text-gray-400 py-8">Loading…</p>}
            {!isLoading && weekDays.map((day, i) => {
              const dateStr = toDateStr(day)
              const dayBookings = byDateStr[dateStr] ?? []
              const isToday = dateStr === todayStr
              const isUnavailable = unavailableForDate(dateStr).length > 0
              return (
                <div key={dateStr}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{dayNames[i]}</span>
                    <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-bold ${
                      isToday ? 'bg-indigo-600 text-white' : 'text-gray-800'
                    }`}>
                      {day.getDate()}
                    </span>
                    {dayBookings.length > 0 && (
                      <span className="text-xs text-gray-400">{dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}</span>
                    )}
                    {isUnavailable && (
                      <span className="flex items-center gap-1 text-xs text-gray-400"><CalendarOff size={11} />Unavailable</span>
                    )}
                  </div>
                  {dayBookings.length === 0
                    ? <p className="text-xs text-gray-300">Nothing scheduled</p>
                    : <div className="space-y-1.5">{dayBookings.map(b => <BookingCard key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} basePath={basePath} navigate={navigate} />)}</div>
                  }
                </div>
              )
            })}
          </div>

          {/* Desktop: 7-column grid */}
          <div className="hidden sm:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="grid grid-cols-7 border-b border-gray-100">
              {weekDays.map((day, i) => {
                const dateStr = toDateStr(day)
                const isToday = dateStr === todayStr
                const isSelected = dateStr === selectedDate
                return (
                  <div
                    key={i}
                    onClick={() => toggleDate(dateStr)}
                    className={`py-2.5 text-center border-r border-gray-100 last:border-r-0 cursor-pointer transition-colors ${
                      isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className={`text-xs font-semibold uppercase tracking-wide ${isToday ? 'text-indigo-600' : 'text-gray-400'}`}>
                      {dayNames[i]}
                    </div>
                    <div className={`mx-auto mt-1 w-7 h-7 flex items-center justify-center rounded-full text-sm font-bold ${
                      isToday ? 'bg-indigo-600 text-white' :
                      isSelected ? 'bg-indigo-200 text-indigo-900' :
                      'text-gray-700'
                    }`}>
                      {day.getDate()}
                    </div>
                  </div>
                )
              })}
            </div>
            {isLoading ? (
              <div className="py-24 text-center text-gray-400 text-sm">Loading…</div>
            ) : (
              <div className="grid grid-cols-7">
                {weekDays.map((day, i) => {
                  const dateStr = toDateStr(day)
                  const isToday = dateStr === todayStr
                  const isSelected = dateStr === selectedDate
                  const dayBookings = byDateStr[dateStr] ?? []
                  const isWeekend = day.getDay() === 0 || day.getDay() === 6
                  const isUnavailable = unavailableForDate(dateStr).length > 0
                  return (
                    <div
                      key={i}
                      className={`min-h-[150px] border-r border-gray-100 p-2 last:border-r-0 ${
                        isSelected ? 'bg-indigo-50/60' :
                        isUnavailable ? 'bg-gray-100' :
                        isToday ? 'bg-indigo-50/30' :
                        isWeekend ? 'bg-slate-50/60' : 'bg-white'
                      }`}
                    >
                      {isUnavailable && (
                        <div className="flex items-center gap-1 text-[10px] text-gray-400 mb-1">
                          <CalendarOff size={10} />Unavailable
                        </div>
                      )}
                      <div className="space-y-1">
                        {dayBookings.map(b => <BookingChip key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} basePath={basePath} navigate={navigate} />)}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── DAY PANEL (modal) — an in-flow panel below a tall month grid could land below the
          fold, hiding the unavailability toggle entirely; a modal is visible no matter where in
          the grid you clicked. ── */}
      {selectedDate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSelectedDate(null)}>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
            <h3 className="font-semibold text-gray-900 text-sm">{formatDayHeading(selectedDate)}</h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-gray-600 cursor-pointer p-0.5 rounded transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div className="p-4 space-y-3 overflow-y-auto">
            {/* Unavailability toggle for this day */}
            {unavailableForDate(selectedDate).length > 0 ? (
              <div className="space-y-1.5">
                {unavailableForDate(selectedDate).map(u => (
                  <div key={u.id} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="flex items-center gap-1.5 text-sm text-gray-600">
                      <CalendarOff size={14} className="text-gray-400 shrink-0" />
                      Unavailable{u.reason ? `: ${u.reason}` : ''}
                      {u.startDate !== u.endDate && (
                        <span className="text-xs text-gray-400">({formatDate(u.startDate, { day: 'numeric', month: 'short' })} – {formatDate(u.endDate, { day: 'numeric', month: 'short' })})</span>
                      )}
                      {u.recurrenceGroupId && <span className="text-xs text-gray-400">(recurring)</span>}
                    </span>
                    {u.recurrenceGroupId ? (
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => removeUnavailabilityMutation.mutate(u.id)}
                          disabled={removeUnavailabilityMutation.isPending || removeUnavailabilityGroupMutation.isPending}
                          className="text-xs text-gray-400 hover:text-red-600 cursor-pointer disabled:opacity-50"
                        >
                          Remove this day
                        </button>
                        <button
                          onClick={() => removeUnavailabilityGroupMutation.mutate(u.recurrenceGroupId!)}
                          disabled={removeUnavailabilityMutation.isPending || removeUnavailabilityGroupMutation.isPending}
                          className="text-xs text-gray-400 hover:text-red-600 cursor-pointer disabled:opacity-50"
                        >
                          Remove whole series
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => removeUnavailabilityMutation.mutate(u.id)}
                        disabled={removeUnavailabilityMutation.isPending}
                        className="text-xs text-gray-400 hover:text-red-600 cursor-pointer disabled:opacity-50 shrink-0"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <button
                onClick={() => quickAddMutation.mutate(selectedDate)}
                disabled={quickAddMutation.isPending}
                className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 cursor-pointer disabled:opacity-50"
              >
                <CalendarOff size={14} />
                {quickAddMutation.isPending ? 'Marking…' : 'Mark this day unavailable'}
              </button>
            )}

            {selectedDateBookings.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4">Nothing scheduled on this day.</p>
            ) : (
              <div className="space-y-2">
                {selectedDateBookings.map(b => <BookingCard key={b.dayNumber != null ? `${b.id}-${b.dayNumber}` : b.id} b={b} basePath={basePath} navigate={navigate} />)}
              </div>
            )}
          </div>
        </div>
        </div>
      )}
    </div>
  )
}
